import * as THREE from 'three';
import { COLORS, CONTAINER, DANGER_LINE_Y, QUALITY, type QualityLevel } from '../config';

export class SceneRig {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  private containerGroup = new THREE.Group();
  private bgGroup = new THREE.Group();
  private dangerLine: THREE.Mesh | null = null;
  private dangerGlow: THREE.Mesh | null = null;
  private ambient!: THREE.AmbientLight;
  private keyLight!: THREE.DirectionalLight;
  private fillLight!: THREE.DirectionalLight;
  private rimLight!: THREE.PointLight;
  private sparkles: THREE.Points | null = null;
  private time = 0;
  private shakeAmp = 0;
  private baseCam = new THREE.Vector3(0, 0.3, 16.5);

  constructor(canvas: HTMLCanvasElement) {
    this.camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    this.camera.position.set(0, 0.3, 16.5);
    this.camera.lookAt(0, -0.2, 0);

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;

    this.scene.background = new THREE.Color(COLORS.bgDeep);
    this.scene.fog = new THREE.FogExp2(COLORS.bgDeep, 0.018);

    this.buildLights();
    this.buildBackground();
    this.buildContainer();
    this.buildDangerLine();
  }

  setQuality(q: QualityLevel): void {
    const settings = QUALITY[q];
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, settings.pixelRatio));
    this.renderer.shadowMap.enabled = settings.shadows;
    this.keyLight.castShadow = settings.shadows;
    if (settings.shadows) {
      this.keyLight.shadow.mapSize.set(settings.shadowMapSize, settings.shadowMapSize);
    }
    this.renderer.toneMappingExposure = q === 'low' ? 1.0 : 1.05;
  }

  private buildLights(): void {
    this.ambient = new THREE.AmbientLight(0xb8a0e0, 0.45);
    this.scene.add(this.ambient);

    this.keyLight = new THREE.DirectionalLight(0xfff0e0, 1.35);
    this.keyLight.position.set(4, 10, 8);
    this.keyLight.castShadow = true;
    this.keyLight.shadow.mapSize.set(2048, 2048);
    this.keyLight.shadow.camera.near = 1;
    this.keyLight.shadow.camera.far = 40;
    this.keyLight.shadow.camera.left = -10;
    this.keyLight.shadow.camera.right = 10;
    this.keyLight.shadow.camera.top = 12;
    this.keyLight.shadow.camera.bottom = -12;
    this.keyLight.shadow.bias = -0.0008;
    this.scene.add(this.keyLight);

    this.fillLight = new THREE.DirectionalLight(0x8ec5ff, 0.45);
    this.fillLight.position.set(-6, 4, 4);
    this.scene.add(this.fillLight);

    this.rimLight = new THREE.PointLight(0xff9ecd, 0.8, 30);
    this.rimLight.position.set(0, 6, 4);
    this.scene.add(this.rimLight);

    const jarLight = new THREE.PointLight(0xc9a0ff, 0.35, 20);
    jarLight.position.set(0, -2, 3);
    this.scene.add(jarLight);
  }

  private buildBackground(): void {
    // Gradient plane
    const bgGeo = new THREE.PlaneGeometry(60, 40);
    const bgMat = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        colorA: { value: new THREE.Color(0x0d0618) },
        colorB: { value: new THREE.Color(0x2a1050) },
        colorC: { value: new THREE.Color(0x1a3060) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform vec3 colorA;
        uniform vec3 colorB;
        uniform vec3 colorC;
        varying vec2 vUv;
        void main() {
          float w = sin(vUv.x * 3.0 + time * 0.15) * 0.5 + 0.5;
          float h = vUv.y;
          vec3 col = mix(colorA, colorB, h);
          col = mix(col, colorC, w * 0.35 * h);
          // soft nebula blobs
          float n = sin(vUv.x * 8.0 + time * 0.2) * sin(vUv.y * 6.0 - time * 0.12);
          col += vec3(0.15, 0.05, 0.2) * n * 0.15;
          gl_FragColor = vec4(col, 1.0);
        }
      `,
      depthWrite: false,
    });
    const bg = new THREE.Mesh(bgGeo, bgMat);
    bg.position.z = -12;
    bg.name = 'bg';
    this.bgGroup.add(bg);

    // Floating sparkles
    const count = 80;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 28;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 2] = -8 - Math.random() * 6;
    }
    const spGeo = new THREE.BufferGeometry();
    spGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const spMat = new THREE.PointsMaterial({
      color: 0xffe0ff,
      size: 0.08,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.sparkles = new THREE.Points(spGeo, spMat);
    this.bgGroup.add(this.sparkles);

    // Soft ground disc under jar
    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(7, 48),
      new THREE.MeshStandardMaterial({
        color: 0x1a0f30,
        roughness: 0.85,
        metalness: 0.1,
        emissive: 0x120820,
        emissiveIntensity: 0.3,
      }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -CONTAINER.height / 2 - 0.45;
    ground.receiveShadow = true;
    this.bgGroup.add(ground);

    this.scene.add(this.bgGroup);
  }

  private buildContainer(): void {
    const w = CONTAINER.width;
    const h = CONTAINER.height;
    const t = CONTAINER.wallThickness;
    const halfW = w / 2;
    const halfH = h / 2;

    // Glass back panel
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: 0xe8deff,
      transparent: true,
      opacity: 0.12,
      roughness: 0.15,
      metalness: 0.05,
      transmission: 0.7,
      thickness: 0.5,
      side: THREE.DoubleSide,
    });

    const back = new THREE.Mesh(new THREE.PlaneGeometry(w + t * 2, h + t), glassMat);
    back.position.z = -0.35;
    back.receiveShadow = true;
    this.containerGroup.add(back);

    // Soft interior gradient
    const innerMat = new THREE.MeshBasicMaterial({
      color: 0x1a1035,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
    });
    const inner = new THREE.Mesh(new THREE.PlaneGeometry(w * 0.98, h * 0.98), innerMat);
    inner.position.z = -0.34;
    this.containerGroup.add(inner);

    // Walls — stylized candy ceramic
    const wallMat = new THREE.MeshStandardMaterial({
      color: 0xf5e6ff,
      roughness: 0.35,
      metalness: 0.15,
      emissive: 0x3a2060,
      emissiveIntensity: 0.08,
    });
    const rimMat = new THREE.MeshStandardMaterial({
      color: 0xffd6f0,
      roughness: 0.3,
      metalness: 0.25,
      emissive: 0xff9ecd,
      emissiveIntensity: 0.15,
    });

    const makeWall = (hw: number, hh: number, x: number, y: number) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(hw * 2, hh * 2, 0.5), wallMat);
      m.position.set(x, y, 0);
      m.castShadow = true;
      m.receiveShadow = true;
      this.containerGroup.add(m);
    };

    makeWall(t / 2, halfH + 0.2, -halfW - t / 2, 0);
    makeWall(t / 2, halfH + 0.2, halfW + t / 2, 0);
    makeWall(halfW + t, CONTAINER.floorThickness / 2, 0, -halfH - CONTAINER.floorThickness / 2);

    // Decorative rim at top
    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(halfW + t * 0.3, 0.12, 10, 48, Math.PI),
      rimMat,
    );
    rim.rotation.z = Math.PI;
    rim.position.set(0, halfH + 0.05, 0.1);
    // Use box rim instead for cleaner look
    rim.visible = false;
    this.containerGroup.add(rim);

    const topLeft = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.28, 0.55), rimMat);
    topLeft.position.set(-halfW - t / 2, halfH + 0.05, 0);
    const topRight = topLeft.clone();
    topRight.position.x = halfW + t / 2;
    this.containerGroup.add(topLeft, topRight);

    // Floor decorative stripe
    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(w * 0.95, 0.06, 0.52),
      new THREE.MeshStandardMaterial({
        color: 0xc9a0ff,
        emissive: 0x8b5cf6,
        emissiveIntensity: 0.3,
        roughness: 0.4,
      }),
    );
    stripe.position.set(0, -halfH + 0.02, 0.05);
    this.containerGroup.add(stripe);

    // Soft outer frame glow
    const frameGeo = new THREE.PlaneGeometry(w + t * 2 + 0.4, h + t + 0.4);
    const frameMat = new THREE.MeshBasicMaterial({
      color: 0x9b6dff,
      transparent: true,
      opacity: 0.08,
      side: THREE.DoubleSide,
    });
    const frame = new THREE.Mesh(frameGeo, frameMat);
    frame.position.z = -0.4;
    this.containerGroup.add(frame);

    this.scene.add(this.containerGroup);
  }

  private buildDangerLine(): void {
    const w = CONTAINER.width;
    const y = DANGER_LINE_Y;

    const geo = new THREE.PlaneGeometry(w * 0.96, 0.04);
    const mat = new THREE.MeshBasicMaterial({
      color: COLORS.danger,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.dangerLine = new THREE.Mesh(geo, mat);
    this.dangerLine.position.set(0, y, 0.25);
    this.scene.add(this.dangerLine);

    const glowGeo = new THREE.PlaneGeometry(w * 0.96, 0.35);
    const glowMat = new THREE.MeshBasicMaterial({
      color: COLORS.dangerSoft,
      transparent: true,
      opacity: 0.0,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    this.dangerGlow = new THREE.Mesh(glowGeo, glowMat);
    this.dangerGlow.position.set(0, y, 0.24);
    this.scene.add(this.dangerGlow);

    // Dashed feel via second thinner line
    const dash = new THREE.Mesh(
      new THREE.PlaneGeometry(w * 0.96, 0.015),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.35,
        depthWrite: false,
      }),
    );
    dash.position.set(0, y, 0.26);
    this.scene.add(dash);
  }

  setDangerLevel(level: number): void {
    // 0..1
    if (!this.dangerLine || !this.dangerGlow) return;
    const mat = this.dangerLine.material as THREE.MeshBasicMaterial;
    const glow = this.dangerGlow.material as THREE.MeshBasicMaterial;
    mat.opacity = 0.4 + level * 0.55;
    glow.opacity = level * 0.45;
    const pulse = 1 + Math.sin(this.time * 10) * 0.08 * level;
    this.dangerLine.scale.y = pulse;
  }

  resize(width: number, height: number): void {
    this.camera.aspect = width / height;
    // Keep jar framed: adjust FOV slightly on mobile
    if (width < height) {
      this.camera.fov = 48;
      this.baseCam.set(0, 0.3, 18.5);
    } else {
      this.camera.fov = 38;
      this.baseCam.set(0, 0.3, 16.5);
    }
    this.camera.position.copy(this.baseCam);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  shake(amount: number): void {
    this.shakeAmp = Math.max(this.shakeAmp, amount);
  }

  update(dt: number): void {
    this.time += dt;
    const bg = this.bgGroup.getObjectByName('bg') as THREE.Mesh | undefined;
    if (bg) {
      const mat = bg.material as THREE.ShaderMaterial;
      mat.uniforms.time!.value = this.time;
    }
    if (this.sparkles) {
      this.sparkles.rotation.z = this.time * 0.02;
      const positions = this.sparkles.geometry.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < positions.count; i++) {
        let y = positions.getY(i) + dt * (0.1 + (i % 5) * 0.02);
        if (y > 10) y = -10;
        positions.setY(i, y);
      }
      positions.needsUpdate = true;
    }
    this.rimLight.intensity = 0.7 + Math.sin(this.time * 1.3) * 0.15;

    // Camera shake decay
    if (this.shakeAmp > 0.001) {
      this.camera.position.x = this.baseCam.x + (Math.random() - 0.5) * this.shakeAmp;
      this.camera.position.y = this.baseCam.y + (Math.random() - 0.5) * this.shakeAmp;
      this.shakeAmp *= Math.pow(0.02, dt);
    } else {
      this.camera.position.x = this.baseCam.x;
      this.camera.position.y = this.baseCam.y;
      this.shakeAmp = 0;
    }
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  /** Project NDC x (-1..1) to world X at z=0 plane */
  screenToWorldX(ndcX: number, ndcY: number): number {
    const vec = new THREE.Vector3(ndcX, ndcY, 0.5);
    vec.unproject(this.camera);
    const dir = vec.sub(this.camera.position).normalize();
    const dist = -this.camera.position.z / dir.z;
    const world = this.camera.position.clone().add(dir.multiplyScalar(dist));
    return world.x;
  }

  dispose(): void {
    this.renderer.dispose();
  }
}
